import React, { useMemo } from "react";
import { diffLines } from "diff";
import { GitCompareArrows, X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { formatDateTime } from "../../lib/contractUtils";

export function VersionCompareDialog({ open, onOpenChange, oldVersion, newVersion }) {
  const diff = useMemo(() => {
    if (!oldVersion || !newVersion) return [];
    return diffLines(oldVersion.content || "", newVersion.content || "");
  }, [oldVersion, newVersion]);

  const stats = useMemo(() => {
    let added = 0, removed = 0;
    diff.forEach((part) => {
      const lines = part.value.split("\n").filter(Boolean).length;
      if (part.added) added += lines;
      else if (part.removed) removed += lines;
    });
    return { added, removed };
  }, [diff]);

  if (!oldVersion || !newVersion) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 gap-0" aria-describedby="version-compare-desc">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <GitCompareArrows size={18} className="text-indigo-600" />
              </div>
              <div>
                <DialogTitle className="text-base">Version Comparison</DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5" id="version-compare-desc">
                  Version {oldVersion.version} &rarr; Version {newVersion.version}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {stats.added > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 rounded-sm text-xs font-mono" data-testid="diff-added-count">
                  +{stats.added} added
                </Badge>
              )}
              {stats.removed > 0 && (
                <Badge className="bg-red-100 text-red-700 rounded-sm text-xs font-mono" data-testid="diff-removed-count">
                  -{stats.removed} removed
                </Badge>
              )}
              {stats.added === 0 && stats.removed === 0 && (
                <Badge className="bg-slate-100 text-slate-600 rounded-sm text-xs">No changes</Badge>
              )}
            </div>
          </div>

          {/* Version metadata bar */}
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="px-3 py-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-sm">
              <p className="text-xs font-mono text-red-600 uppercase tracking-wider">Old &mdash; Version {oldVersion.version}</p>
              <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(oldVersion.modified_at)} &middot; {oldVersion.comment || "No comment"}</p>
            </div>
            <div className="px-3 py-2 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-sm">
              <p className="text-xs font-mono text-emerald-600 uppercase tracking-wider">New &mdash; Version {newVersion.version}</p>
              <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(newVersion.modified_at)} &middot; {newVersion.comment || "No comment"}</p>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="h-[60vh]">
          <div className="px-6 py-4 font-mono text-sm leading-relaxed" data-testid="diff-content">
            {diff.map((part, i) => (
              <DiffBlock key={i} part={part} />
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function DiffBlock({ part }) {
  const lines = part.value.split("\n");
  // Remove trailing empty line from split
  if (lines[lines.length - 1] === "") lines.pop();

  if (!part.added && !part.removed) {
    // Unchanged — show as context
    return lines.map((line, i) => (
      <div key={i} className="px-3 py-0.5 text-slate-700 dark:text-slate-300 border-l-2 border-transparent">
        {line || "\u00A0"}
      </div>
    ));
  }

  if (part.added) {
    return lines.map((line, i) => (
      <div
        key={i}
        className="px-3 py-0.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 border-l-2 border-emerald-500"
        data-testid="diff-line-added"
      >
        <span className="select-none text-emerald-400 mr-2">+</span>
        {line || "\u00A0"}
      </div>
    ));
  }

  // Removed
  return lines.map((line, i) => (
    <div
      key={i}
      className="px-3 py-0.5 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 border-l-2 border-red-500 line-through decoration-red-300"
      data-testid="diff-line-removed"
    >
      <span className="select-none text-red-400 mr-2">-</span>
      {line || "\u00A0"}
    </div>
  ));
}
